import { useLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { groupFulfillmentOrderLineItems } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { ProductId } from "@redotech/redo-product-id";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import {
  ItemTableHeader,
  ProductListRow,
} from "@redotech/redo-web/product-list-row";
import { memo, useContext, useMemo } from "react";
import { useWindowSize } from "usehooks-ts";
import { CurrentInventoryButton } from "../../../common/current-inventory-button";
import { LineItemLots } from "../../../common/line-item-lots";
import { SplitGroupButton } from "../../../common/split-group-button";
import {
  RedoContentCard,
  RedoContentCardReorderProps,
} from "./redo-content-card";

const ON_HAND_MIN_WIDTH = 900;

export const ItemsCard = memo(function ItemsCard({
  fulfillmentGroup,
  triggerReload,
  everythingDisabled,
  reorderHandleProps,
  showReorderHandle,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  triggerReload: () => Promise<void>;
  everythingDisabled: boolean;
} & RedoContentCardReorderProps) {
  const { width: windowWidth } = useWindowSize();
  const showOnHand = windowWidth >= ON_HAND_MIN_WIDTH;

  const lineItemsQuantity = useMemo(() => {
    return fulfillmentGroup.items.reduce((acc, item) => acc + item.quantity, 0);
  }, [fulfillmentGroup]);

  return (
    <RedoContentCard
      contentSpacing="none"
      headerActions={
        <Flex align="center" gap="sm">
          {!showOnHand && (
            <CurrentInventoryButton fulfillmentGroups={[fulfillmentGroup]} />
          )}
          <SplitGroupButton
            disabled={everythingDisabled}
            fulfillmentGroup={fulfillmentGroup}
            isSlideout={false}
            size="sm"
            triggerReload={triggerReload}
          />
        </Flex>
      }
      reorderHandleProps={reorderHandleProps}
      showReorderHandle={showReorderHandle}
      title={`Items in this shipment (${lineItemsQuantity})`}
    >
      <ItemsCardTable
        fulfillmentGroup={fulfillmentGroup}
        showOnHand={showOnHand}
      />
    </RedoContentCard>
  );
});

interface InventoryLevel {
  onHand: number | null;
  available: number | null;
}

export const ItemsCardTable = memo(function ItemsCardTable({
  fulfillmentGroup,
  showOnHand = false,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  showOnHand?: boolean;
}) {
  const team = useContext(TeamContext);
  const inventoryRpcClient = useContext(RedoInventoryRpcClientContext);
  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;
  const hideUnderscoreCustomAttributes =
    !!team?.settings?.outboundLabels?.hideUnderscoreCustomAttributes;
  const items = useMemo(() => {
    return groupFulfillmentOrderLineItems(fulfillmentGroup.items, {
      degroupByCustomAttributes: showCustomAttributes,
    });
  }, [fulfillmentGroup, showCustomAttributes]);

  const locationName = fulfillmentGroup.originAddress.name;

  const inventoryLoad = useLoad(
    async (signal) => {
      if (!showOnHand || !inventoryRpcClient) {
        return null;
      }
      const productIds = items
        .map((item) => item.redoProductId)
        .filter((id): id is ProductId => id != null);
      if (productIds.length === 0) {
        return null;
      }
      const { items: levels } =
        await inventoryRpcClient.getProductInventoryLevels(
          { productIds },
          { signal },
        );
      const map = new Map<string, InventoryLevel>();
      for (const level of levels) {
        if (level.locationName === locationName) {
          map.set(level.productId, {
            onHand: level.quantity == null ? null : Number(level.quantity),
            available: level.available == null ? null : Number(level.available),
          });
        }
      }
      return map;
    },
    [showOnHand, inventoryRpcClient, items, locationName],
  );

  const inventoryByProductId = inventoryLoad.value;
  const showInventoryColumns = showOnHand && inventoryByProductId != null;

  return (
    <Flex dir="column" gap="none" height="fit-content" w="full">
      <ItemTableHeader
        showAmountPaid
        showAvailable={showInventoryColumns}
        showOnHand={showInventoryColumns}
      />
      <Flex dir="column" flex="1" gap="none" w="full">
        {items.map((item, index) => {
          const unitDiscountedPrice = Number(item.unitDiscountedPrice.amount);
          const amountPaidMoney = toMoney({
            amount: Number.isFinite(unitDiscountedPrice)
              ? unitDiscountedPrice * item.quantity
              : null,
            currency: item.unitDiscountedPrice.currency,
          });
          const inventoryLevel =
            showInventoryColumns && item.redoProductId != null
              ? inventoryByProductId.get(item.redoProductId)
              : undefined;

          return (
            <Flex
              borderBottomWidth={index !== items.length - 1 ? "1px" : "none"}
              borderColor="secondary"
              borderStyle="solid"
              dir="column"
              gap="none"
              key={index}
              w="full"
            >
              <ProductListRow
                amountPaidMoney={amountPaidMoney}
                available={inventoryLevel?.available ?? null}
                customAttributes={
                  showCustomAttributes
                    ? hideUnderscoreCustomAttributes
                      ? item.customAttributes?.filter(
                          (attr) => !attr.key.startsWith("_"),
                        )
                      : item.customAttributes
                    : undefined
                }
                imageTooltipPlacement="top"
                imageUrl={item.imageUrl}
                onHand={inventoryLevel?.onHand ?? null}
                price={item.unitDiscountedPrice.amount}
                priceMoney={toMoney(item.unitDiscountedPrice)}
                quantity={item.quantity}
                showAvailable={showInventoryColumns}
                showOnHand={showInventoryColumns}
                sku={item.sku}
                title={item.productTitle || item.title}
                variantTitle={item.variantTitle}
              />
              {item.lots?.length ? (
                <Flex pb="lg" px="xl" w="full">
                  <LineItemLots lots={item.lots} />
                </Flex>
              ) : null}
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
});
